const MOBILE_UA_RE = /Android|iPhone|iPad|iPod/i;
export const IOS_APP_STORE_URL = 'https://apps.apple.com/jp/app/casticart-mobile/id6770538865';
export const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.casticart.mobile';
export const ANDROID_PACKAGE_NAME = 'com.casticart.mobile';
export const APP_SCHEME = 'casticart-mobile';

const trimTrailingSlash = (value = '') => String(value || '').replace(/\/+$/, '');

const safeDecode = (value) => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch {
    return String(value || '');
  }
};

const segmentValue = (segments, index) => {
  const value = segments[index];
  return value ? safeDecode(value) : '';
};

const normalizeSearchParamValue = (value) => {
  if (value === null || value === undefined) return '';
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return '';
  return trimmed;
};

const firstSearchParam = (searchParams, keys) => {
  for (const key of keys) {
    const value = normalizeSearchParamValue(searchParams.get(key));
    if (value) return value;
  }
  return '';
};

const withChatParams = (searchParams, params = {}) => ({
  ...params,
  threadId: firstSearchParam(searchParams, ['threadId', 'thread', 'thread_id']),
  anchorMessageId: firstSearchParam(searchParams, [
    'anchorMessageId',
    'messageId',
    'message_id',
  ]),
});

const withParams = (root, screen, params = {}) => ({
  root,
  screen,
  params: Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && String(value) !== ''
    )
  ),
});

export const isMobileBrowserForAppLink = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (MOBILE_UA_RE.test(ua)) return true;
  return Boolean(window.matchMedia?.('(max-width: 900px)')?.matches);
};

export const resolveOpenInAppPlatform = ({ userAgent, platform, maxTouchPoints } = {}) => {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const ua = String(userAgent ?? nav.userAgent ?? '');
  const navPlatform = String(platform ?? nav.platform ?? '');
  const touchPoints = Number(maxTouchPoints ?? nav.maxTouchPoints ?? 0);
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Mac/i.test(navPlatform) && touchPoints > 1) return 'ios';
  return null;
};

export const getOpenInAppStoreUrl = (platform = resolveOpenInAppPlatform()) => {
  if (platform === 'ios') return IOS_APP_STORE_URL;
  if (platform === 'android') return ANDROID_PLAY_STORE_URL;
  return null;
};

export const getOpenInAppOrigin = () => {
  if (process.env.REACT_APP_FRONTEND_ORIGIN) {
    return trimTrailingSlash(process.env.REACT_APP_FRONTEND_ORIGIN);
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return trimTrailingSlash(window.location.origin);
  }
  return '';
};

const resolveAdminRoute = (segments, searchParams) => {
  const section = segmentValue(segments, 1);
  if (!section) return withParams('Admin', 'AdminHome');

  if (section === 'tasks') {
    const taskTarget = segmentValue(segments, 2);
    if (!taskTarget) return withParams('Admin', 'AdminTaskHome');
    if (taskTarget === 'quick-tasks') return withParams('Admin', 'AdminQuickTasks');
    if (taskTarget === 'events') return withParams('Admin', 'AdminTaskEvents');
    if (taskTarget === 'templates' && segmentValue(segments, 3) === 'new') {
      return withParams('Admin', 'AdminTaskTemplates');
    }
    return withParams('Admin', 'AdminTaskProjectDetail', { projectId: taskTarget });
  }

  if (section === 'artists') {
    const target = segmentValue(segments, 2);
    if (!target || target === 'list') return withParams('Admin', 'AdminArtists');
    if (target === 'register') return withParams('Admin', 'AdminArtistRegistration');
    if (target === 'registration-complete') {
      return withParams('Admin', 'AdminArtistRegistrationComplete');
    }
    if (target === 'group') return withParams('Admin', 'AdminArtistGroups');
    return withParams('Admin', 'AdminArtistDetail', { artistId: target });
  }

  if (section === 'venues') {
    const target = segmentValue(segments, 2);
    if (!target) return withParams('Admin', 'AdminVenues');
    if (target === 'register') return withParams('Admin', 'AdminVenueRegistration');
    return withParams('Admin', 'AdminVenueDetail', { venueId: target, role: 'admin' });
  }

  if (section === 'events') {
    const target = segmentValue(segments, 2);
    const detailId = segmentValue(segments, 3);
    if (!target) return withParams('Admin', 'AdminEvents');
    if (target === 'register') return withParams('Admin', 'AdminEventRegistration');
    if (target === 'registration-complete') {
      return withParams('Admin', 'AdminEventRegistrationComplete');
    }
    if (target === 'event-details' && detailId) {
      return withParams('Admin', 'AdminEventDetail', { eventId: detailId, role: 'admin' });
    }
    if (target === 'edit' && detailId) {
      return withParams('Admin', 'AdminEventEdit', { eventId: detailId });
    }
    if (target === 'event-casting-registration' && detailId) {
      return withParams('Admin', 'AdminEventCasting', { eventId: detailId });
    }

    const eventId = target;
    const eventAction = segmentValue(segments, 3);
    const subAction = segmentValue(segments, 4);
    if (eventAction === 'casting-progress') {
      return withParams('Admin', 'AdminCastingProgress', { eventId });
    }
    if (eventAction === 'ticket-sales' && subAction === 'analytics') {
      return withParams('Admin', 'AdminEventTicketSalesAnalytics', { eventId });
    }
    if (eventAction === 'ticket-sales') {
      return withParams('Admin', 'AdminEventTicketSales', { eventId });
    }
    if (eventAction === 'pl') return withParams('Admin', 'AdminEventProfitLoss', { eventId });
    if (eventAction === 'timetable') return withParams('Admin', 'AdminEventTimetable', { eventId });
    if (eventAction === 'timetable-view' && subAction) {
      return withParams('Admin', 'AdminEventTimetableView', {
        eventId,
        timetableId: subAction,
        role: 'admin',
      });
    }
    if (eventAction === 'participant-artists') {
      return withParams('Admin', 'AdminParticipantInfo', { eventId });
    }
    if (eventAction === 'stage-seating') {
      return withParams('Admin', 'AdminEventStageSeating', {
        eventId,
        role: 'admin',
        layoutId: searchParams.get('layout_id') || searchParams.get('layoutId') || '',
      });
    }
  }

  if (section === 'artist-chat') {
    const artistId = firstSearchParam(searchParams, ['artistId', 'artist_id']);
    return artistId
      ? withParams(
          'Admin',
          'AdminChatThread',
          withChatParams(searchParams, { role: 'admin', chatType: 'artist', artistId })
        )
      : withParams('Admin', 'AdminChat', { role: 'admin', chatType: 'artist' });
  }
  if (section === 'event-chat') {
    const eventId = firstSearchParam(searchParams, ['eventId', 'event', 'event_id']);
    return eventId
      ? withParams(
          'Admin',
          'AdminChatThread',
          withChatParams(searchParams, { role: 'admin', chatType: 'event', eventId })
        )
      : withParams('Admin', 'AdminChat', { role: 'admin', chatType: 'event' });
  }
  if (section === 'group-chat') {
    return withParams('Admin', 'AdminGroupChat', withChatParams(searchParams, { role: 'admin' }));
  }

  if (section === 'casting') {
    const action = segmentValue(segments, 2);
    if (action === 'history') return withParams('Admin', 'AdminOfferHistory');
    if (action === 'hold') return withParams('Admin', 'AdminOfferCreate');
  }
  if (section === 'hold-casting-detail') {
    const target =
      segmentValue(segments, 2) === 'by-hold' ? segmentValue(segments, 3) : segmentValue(segments, 2);
    return target ? withParams('Admin', 'AdminOfferDetail', { holdCastingId: target }) : null;
  }

  if (section === 'contract') {
    const action = segmentValue(segments, 2);
    const target = segmentValue(segments, 3);
    if (action === 'history') return withParams('Admin', 'AdminContractHistory');
    if (action === 'form') return withParams('Admin', 'AdminContractCreate');
    if (action === 'confirm') return withParams('Admin', 'AdminContractConfirm');
    if (action === 'success') return withParams('Admin', 'AdminContractSuccess');
    if (action === 'detail' && target) {
      return withParams('Admin', 'AdminContractDetail', { contractArtistId: target });
    }
    if (action === 'schedule-edit' && target) {
      return withParams('Admin', 'AdminContractEdit', { contractArtistId: target, scheduleOnly: true });
    }
    if (action === 'edit' && target === 'confirm') {
      const scope = segmentValue(segments, 4);
      const contractArtistId = segmentValue(segments, 5);
      if (contractArtistId) {
        return withParams('Admin', 'AdminContractEdit', {
          contractArtistId,
          initialScope: scope === 'personal' ? 'personal' : scope === 'common' ? 'common' : undefined,
        });
      }
    }
    if (action === 'edit' && target) {
      return withParams('Admin', 'AdminContractEdit', { contractArtistId: target });
    }
  }

  if (section === 'finance') return withParams('Admin', 'AdminFinanceDashboard');
  if (section === 'ticket-sales') return withParams('Admin', 'AdminTicketSales');
  if (section === 'payments') {
    const action = segmentValue(segments, 2);
    const target = segmentValue(segments, 3);
    if (action === 'invoice' && target) {
      return withParams('Admin', 'AdminInvoicePreview', { contractArtistId: target });
    }
    return withParams('Admin', 'AdminPayments');
  }
  if (section === 'account-management') return withParams('Admin', 'AdminAccountManagement');
  if (section === 'operational-emails') return withParams('Admin', 'AdminOperationalEmails');
  if (section === 'mobile-release') return withParams('Admin', 'AdminMobileRelease');
  if (section === 'mac-release') return withParams('Admin', 'AdminMacRelease');
  if (section === 'service-ai' || section === 'ai-planner') {
    return withParams('Admin', 'AdminServiceAi');
  }
  if (section === 'service-ai-settings') {
    return withParams('Admin', 'AdminServiceAiSettings');
  }
  if (section === 'announcements') return withParams('Admin', 'AdminAnnouncements');
  if (section === 'policies') return withParams('Admin', 'AdminPolicyEditor');
  if (section === 'action-logs') return withParams('Admin', 'AdminActionLog');
  if (section === 'parts-edit') return withParams('Admin', 'AdminPartsEdit');
  if (section === 'group-tags-edit') return withParams('Admin', 'AdminGroupTags');
  if (section === 'profile-edit') return withParams('Admin', 'AdminProfileEdit');
  if (section === 'notification-settings') return withParams('Admin', 'AdminNotifications');
  if (section === 'change-password') return withParams('Admin', 'AdminChangePassword');
  if (section === 'bugs') return withParams('Admin', 'AdminBugReports');
  if (section === 'manuals') return withParams('Admin', 'AdminManuals', { slug: segmentValue(segments, 2) });
  if (section === 'concierge') return withParams('Admin', 'AdminConciergeList');
  return null;
};

const resolveArtistRoute = (segments, searchParams) => {
  const section = segmentValue(segments, 1);
  if (!section) return withParams('Artist', 'ArtistHome');

  if (section === 'events') {
    const target = segmentValue(segments, 2);
    const detailId = segmentValue(segments, 3);
    if (!target) return withParams('Artist', 'ArtistEvents');
    if (target === 'event-details' && detailId) {
      return withParams('Artist', 'ArtistEventDetail', { eventId: detailId, role: 'artist' });
    }
    const eventId = target;
    const action = segmentValue(segments, 3);
    const subAction = segmentValue(segments, 4);
    if (action === 'timetable-view' && subAction) {
      return withParams('Artist', 'ArtistEventTimetable', {
        eventId,
        timetableId: subAction,
        role: 'artist',
      });
    }
    if (action === 'stage-seating') {
      return withParams('Artist', 'ArtistEventStageSeating', {
        eventId,
        role: 'artist',
        layoutId: searchParams.get('layout_id') || searchParams.get('layoutId') || '',
      });
    }
  }

  if (section === 'chat') {
    const threadId = firstSearchParam(searchParams, ['threadId', 'thread', 'thread_id']);
    return threadId
      ? withParams('Artist', 'ArtistGroupChat', withChatParams(searchParams, { role: 'artist' }))
      : withParams('Artist', 'ArtistChat', { role: 'artist' });
  }
  if (section === 'event-chat') {
    const eventId = firstSearchParam(searchParams, ['eventId', 'event', 'event_id']);
    return eventId
      ? withParams(
          'Artist',
          'ArtistChatThread',
          withChatParams(searchParams, { role: 'artist', chatType: 'event', eventId })
        )
      : withParams('Artist', 'ArtistChat', { role: 'artist', chatType: 'event' });
  }
  if (section === 'offers') return withParams('Artist', 'ArtistOffers');
  if (section === 'offer') {
    const offerId = segmentValue(segments, 2);
    return offerId ? withParams('Artist', 'ArtistOfferDetail', { offerId }) : null;
  }
  if (section === 'contracts') return withParams('Artist', 'ArtistContracts');
  if (section === 'contract') {
    const action = segmentValue(segments, 2);
    const contractArtistId = segmentValue(segments, 3);
    if (action === 'detail' && contractArtistId) {
      return withParams('Artist', 'ArtistContractDetail', { contractArtistId });
    }
    if (action === 'print' && contractArtistId) {
      return withParams('Artist', 'ArtistContractPrint', { contractArtistId });
    }
  }
  if (section === 'venue') {
    const venueId = segmentValue(segments, 2);
    return venueId ? withParams('Artist', 'ArtistVenueDetail', { venueId, role: 'artist' }) : null;
  }
  if (section === 'profile') return withParams('Artist', 'ArtistProfile');
  if (section === 'notification-settings') return withParams('Artist', 'ArtistNotifications');
  if (section === 'change-password') return withParams('Artist', 'ArtistChangePassword');
  if (section === 'bank-info') return withParams('Artist', 'ArtistBankInfo');
  if (section === 'artist-photography') return withParams('Artist', 'ArtistPhotography');
  if (section === 'bug-reports') return withParams('Artist', 'ArtistBugReports');
  if (section === 'manuals') return withParams('Artist', 'ArtistManuals', { slug: segmentValue(segments, 2) });
  if (section === 'rewards') return withParams('Artist', 'ArtistRewards');
  if (section === 'concierge') {
    const action = segmentValue(segments, 2);
    if (!action) return withParams('Artist', 'ArtistConcierge');
    if (action === 'apply') return withParams('Artist', 'ArtistConciergeApply');
    if (action === 'additional-info') {
      const token = segmentValue(segments, 3);
      return token ? withParams('Artist', 'ArtistConciergeAdditionalInfo', { token }) : null;
    }
  }
  if (section === 'email-change') return withParams('Artist', 'ArtistEmailChange');
  return null;
};

const normalizeAdminWorkspaceSegments = (segments) => {
  if (segments[0] !== 'admin') return segments;
  if (segments[1] === 'settings') {
    const settingsMap = {
      profile: ['profile-edit'],
      notifications: ['notification-settings'],
      'change-password': ['change-password'],
    };
    return ['admin-route', ...(settingsMap[segments[2]] || [])];
  }
  if (segments[1] !== 'workspaces') return segments;

  const workspace = segments[2];
  const rest = segments.slice(3);
  const routeSegments = (...items) => ['admin-route', ...items.filter(Boolean)];

  if (workspace === 'event-operations') {
    if (rest[0] === 'dashboard') return routeSegments();
    if (rest[0] === 'events') {
      const eventId = rest[1];
      const action = rest[2];
      const subAction = rest[3];
      if (!eventId) return routeSegments('events');
      if (eventId === 'register' || eventId === 'registration-complete') return routeSegments('events', eventId);
      if (!action) return routeSegments('events', 'event-details', eventId);
      if (action === 'casting-registration') return routeSegments('events', 'event-casting-registration', eventId);
      if (action === 'edit') return routeSegments('events', 'edit', eventId);
      if (action === 'timetables' && subAction) return routeSegments('events', eventId, 'timetable-view', subAction);
      return routeSegments('events', eventId, action, subAction);
    }
    if (rest[0] === 'event-chat') return routeSegments('event-chat');
    if (rest[0] === 'ticket-sales') return routeSegments('ticket-sales');
    if (rest[0] === 'event-options') return routeSegments('event-options', 'edit');
    if (rest[0] === 'genres') return routeSegments('genres', 'edit');
    if (rest[0] === 'venues') return routeSegments('venues', ...rest.slice(1));
  }

  if (workspace === 'task-management') {
    if (rest[0] === 'dashboard') return routeSegments('tasks');
    if (rest[0] === 'projects' && rest[1]) return routeSegments('tasks', rest[1]);
    return routeSegments('tasks', ...rest);
  }
  if (workspace === 'artist-management') {
    if (rest[0] === 'artists') return rest[1] ? routeSegments('artists', rest[1]) : routeSegments('artists', 'list');
    if (rest[0] === 'groups') return routeSegments('artists', 'group');
    if (rest[0] === 'parts') return routeSegments('parts-edit');
    if (rest[0] === 'group-tags') return routeSegments('group-tags-edit');
  }
  if (workspace === 'communications') {
    if (rest[0] === 'artist-chat') return routeSegments('artist-chat', ...rest.slice(1));
    if (rest[0] === 'group-chat') return routeSegments('group-chat');
  }
  if (workspace === 'casting' && rest[0] === 'offers') {
    if (rest[1] === 'new') return routeSegments('casting', 'hold');
    if (rest[1] === 'by-hold' && rest[2]) return routeSegments('hold-casting-detail', 'by-hold', rest[2]);
    if (rest[1]) return routeSegments('hold-casting-detail', rest[1]);
    return routeSegments('casting', 'history');
  }
  if (workspace === 'contracts') {
    const target = rest[0];
    if (!target || target === 'history' || target === 'form' || target === 'confirm' || target === 'success') {
      return routeSegments('contract', ...rest);
    }
    if (rest[1] === 'schedule-edit') return routeSegments('contract', 'schedule-edit', target);
    if (rest[1] === 'edit' && rest[2] === 'confirm') return routeSegments('contract', 'edit', 'confirm', rest[3], target);
    if (rest[1] === 'edit') return routeSegments('contract', 'edit', target);
    return routeSegments('contract', 'detail', target);
  }
  if (workspace === 'finance') {
    if (rest[0] === 'dashboard') return routeSegments('finance');
    if (rest[0] === 'payments' && rest[1] === 'invoices' && rest[2]) return routeSegments('payments', 'invoice', rest[2]);
    return routeSegments(...rest);
  }
  if (workspace === 'service-ai') return routeSegments('service-ai');

  const workspaceMap = {
    library: { music: ['music'] },
    concierge: { dashboard: ['concierge'] },
    planning: { 'ai-planner': ['service-ai'] },
    system: {
      announcements: ['announcements'],
      'operational-emails': ['operational-emails'],
      bugs: ['bugs'],
      'account-management': ['account-management'],
      policies: ['policies'],
      'mobile-release': ['mobile-release'],
      'mac-release': ['mac-release'],
      'action-logs': ['action-logs'],
      'service-ai-settings': ['service-ai-settings'],
      manuals: ['manuals', ...rest.slice(1)],
    },
  };
  const mapped = workspaceMap[workspace]?.[rest[0]];
  return mapped ? routeSegments(...mapped) : segments;
};

const normalizeArtistWorkspaceSegments = (segments) => {
  if (segments[0] !== 'artist') return segments;
  if (segments[1] === 'settings') {
    const settingsMap = {
      profile: ['profile'],
      'email-change': ['email-change'],
      'bank-info': ['bank-info'],
      notifications: ['notification-settings'],
      'change-password': ['change-password'],
      'artist-photography': ['artist-photography'],
    };
    return ['artist-route', ...(settingsMap[segments[2]] || [])];
  }
  if (segments[1] !== 'workspaces') return segments;

  const workspace = segments[2];
  const rest = segments.slice(3);
  const routeSegments = (...items) => ['artist-route', ...items.filter(Boolean)];

  if (workspace === 'event-operations') {
    if (rest[0] === 'dashboard') return routeSegments();
    if (rest[0] === 'events') {
      const eventId = rest[1];
      const action = rest[2];
      const subAction = rest[3];
      if (!eventId) return routeSegments('events');
      if (!action) return routeSegments('events', 'event-details', eventId);
      if (action === 'timetables' && subAction) return routeSegments('events', eventId, 'timetable-view', subAction);
      return routeSegments('events', eventId, action, subAction);
    }
    if (rest[0] === 'event-chat') return routeSegments('event-chat');
    if (rest[0] === 'venues' && rest[1]) return routeSegments('venue', rest[1]);
  }
  if (workspace === 'communications' && rest[0] === 'chat') return routeSegments('chat');
  if (workspace === 'casting' && rest[0] === 'offers') return rest[1] ? routeSegments('offer', rest[1]) : routeSegments('offers');
  if (workspace === 'contracts') {
    const target = rest[0];
    if (target === 'dashboard' || !target) return routeSegments('contracts');
    if (rest[1] === 'print') return routeSegments('contract', 'print', target);
    return routeSegments('contract', 'detail', target);
  }
  if (workspace === 'concierge') return routeSegments('concierge', ...rest.filter((item) => item !== 'dashboard'));
  if (workspace === 'finance') return routeSegments(...rest);
  if (workspace === 'support') return routeSegments(...rest);
  return segments;
};

const normalizeWorkspaceSegments = (segments) => {
  if (segments[0] === 'admin') return normalizeAdminWorkspaceSegments(segments);
  if (segments[0] === 'artist') return normalizeArtistWorkspaceSegments(segments);
  return segments;
};

export const resolveOpenInAppRoute = ({ pathname = '', search = '' } = {}) => {
  const rawSegments = String(pathname || '')
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (
    rawSegments[0] === 'admin-dashboard' ||
    rawSegments[0] === 'artist-dashboard' ||
    rawSegments[0] === 'admin-route' ||
    rawSegments[0] === 'artist-route'
  ) {
    return null;
  }
  const segments = normalizeWorkspaceSegments(rawSegments);
  if (!segments.length) return null;
  const searchParams = new URLSearchParams(search || '');
  if (segments[0] === 'admin-route') return resolveAdminRoute(segments, searchParams);
  if (segments[0] === 'artist-route') return resolveArtistRoute(segments, searchParams);
  return null;
};

export const buildOpenInAppUrl = ({ pathname = '', search = '', origin } = {}) => {
  const route = resolveOpenInAppRoute({ pathname, search });
  const baseOrigin = trimTrailingSlash(origin || getOpenInAppOrigin());
  if (!route || !baseOrigin) return null;
  const url = new URL(`/${route.root}/${route.screen}`, baseOrigin);
  Object.entries(route.params || {}).forEach(([key, value]) => {
    url.searchParams.set(key, String(value));
  });
  url.searchParams.set('source', 'web_banner');
  return {
    href: url.toString(),
    route,
  };
};

export const buildOpenInAppSchemeUrl = (route) => {
  if (!route?.root || !route?.screen) return null;
  const url = new URL(`${APP_SCHEME}://${route.root}/${route.screen}`);
  Object.entries(route.params || {}).forEach(([key, value]) => {
    url.searchParams.set(key, String(value));
  });
  url.searchParams.set('source', 'web_banner');
  return url.toString();
};

export const buildAndroidIntentUrl = (appLinkHref, fallbackUrl = ANDROID_PLAY_STORE_URL) => {
  if (!appLinkHref) return null;
  try {
    const appUrl = new URL(appLinkHref);
    const path = `${appUrl.host}${appUrl.pathname}${appUrl.search}`;
    return `intent://${path}#Intent;scheme=${appUrl.protocol.replace(':', '')};package=${ANDROID_PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
  } catch {
    return null;
  }
};

export const buildOpenInAppLaunchTarget = (appLink, platform = resolveOpenInAppPlatform()) => {
  if (!appLink?.href) return null;
  const storeHref = getOpenInAppStoreUrl(platform);
  if (platform === 'android') {
    return {
      href: buildAndroidIntentUrl(appLink.href, storeHref) || appLink.href,
      storeHref,
      platform,
    };
  }
  if (platform === 'ios') {
    return {
      href: buildOpenInAppSchemeUrl(appLink.route) || appLink.href,
      storeHref,
      platform,
    };
  }
  return {
    href: appLink.href,
    storeHref,
    platform,
  };
};

export const launchOpenInAppTarget = (launchTarget, { fallbackDelayMs = 1500 } = {}) => {
  if (!launchTarget?.href || typeof window === 'undefined') return false;

  let leftPage = false;
  let fallbackTimer = null;
  const cleanup = () => {
    window.removeEventListener('pagehide', markLeftPage);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    }
  };
  const openStoreFallback = () => {
    cleanup();
    if (leftPage || document.visibilityState === 'hidden' || !launchTarget.storeHref) return;
    window.location.assign(launchTarget.storeHref);
  };
  function markLeftPage() {
    leftPage = true;
    cleanup();
  }
  function handleVisibilityChange() {
    if (document.visibilityState === 'hidden') markLeftPage();
  }

  window.addEventListener('pagehide', markLeftPage, { once: true });
  document.addEventListener('visibilitychange', handleVisibilityChange);
  if (launchTarget.storeHref) {
    fallbackTimer = window.setTimeout(openStoreFallback, fallbackDelayMs);
  }
  window.location.assign(launchTarget.href);
  return true;
};
